import { useCallback } from 'react';

import { CONFIRM_POPUP_MODE, useConfirmPopup } from 'src/components/ConfirmPopup';
import { isSameLanguage } from 'src/data/generation';
import * as events from 'src/lib/events';
import { formatBrandVoiceChildRoute } from 'src/lib/routing';
import { ROUTES } from 'src/routes';
import {
  useBrandVoices,
  useHistoryPushOrInfinityOpen,
  useIsApplicationModeInfinity,
  useSetupProjectVocabulary,
  useTrackingEvent,
} from 'src/services';
import { useBrandTermsActions } from 'src/store';

export const useHandlers = ({ brandVoicesAssets, language }) => {
  const { defaultBrandVoice, brandVoices } = useBrandVoices();
  const brandTermsActions = useBrandTermsActions();
  const isApplicationModeInfinity = useIsApplicationModeInfinity();
  const confirmPopup = useConfirmPopup();
  const historyPushOrInfinityOpen = useHistoryPushOrInfinityOpen();
  const trackEvent = useTrackingEvent();

  const setupProjectVocabulary = useSetupProjectVocabulary({ language });

  const brandVoiceForLanguage = brandVoices.find((bv) => isSameLanguage(bv.language, language));

  const handleSettingsClick = useCallback(() => {
    setupProjectVocabulary();
  }, [setupProjectVocabulary]);

  const handleActivityToggle = useCallback(() => {
    const wasBrandRulesEnabled = brandTermsActions.getBrandRulesEnabled();
    const brandVoiceIdsForBrandTerms = brandTermsActions.getBrandVoiceIdsForBrandTerms();

    brandTermsActions.toggleBrandRulesActivity();

    if (!wasBrandRulesEnabled && brandVoiceIdsForBrandTerms.length === 0) {
      const brandVoiceIds = brandVoicesAssets.map((brandVoicesAsset) => brandVoicesAsset.id);
      brandTermsActions.setBrandVoiceIdsForBrandTerms(brandVoiceIds);
      setupProjectVocabulary();
    }

    trackEvent(events.PROJECT.toggledBrandRules, {
      enabled: brandTermsActions.getBrandRulesEnabled(),
      page: isApplicationModeInfinity ? events.EVENT_PAGE.infinity : events.EVENT_PAGE.editor,
    });
  }, [
    brandTermsActions,
    trackEvent,
    isApplicationModeInfinity,
    brandVoicesAssets,
    setupProjectVocabulary,
  ]);

  const handleDefineBrandRules = useCallback(async () => {
    const confirmed = await confirmPopup({
      mode: CONFIRM_POPUP_MODE.confirmation,
      title: 'Brand vocabulary',
      content:
        'Set clear guidelines by flagging terms you would like Anyword to use and terms you would like Anyword to refrain from using.',
      buttons: {
        confirm: 'Create vocabulary',
        cancel: 'Cancel',
      },
    });
    if (!confirmed) {
      return;
    }

    const brandVoiceToUse =
      brandVoiceForLanguage && defaultBrandVoice?.language !== language
        ? brandVoiceForLanguage
        : defaultBrandVoice;
    historyPushOrInfinityOpen(formatBrandVoiceChildRoute(brandVoiceToUse.id, ROUTES.BRAND_TERMS));
  }, [confirmPopup, defaultBrandVoice, historyPushOrInfinityOpen, brandVoiceForLanguage, language]);

  return {
    handleSettingsClick,
    handleActivityToggle,
    handleDefineBrandRules,
  };
};
